import api from "./api";

// ── Comercial ────────────────────────────────────────────────────────────────
export interface CommercialBillingParams {
  period?: "year" | "12m" | "all" | "custom";
  from?: string;
  to?: string;
  client?: string;
  agent?: string;
  type?: number;
  /** "Emular cliente". */
  companyId?: string;
}

export interface CommercialClientRow {
  client: string;
  gross: number;
  net: number;
  vmld: number;
  processes: number;
  averageTicket: number;
  participation: number;
  vsPreviousYear: number | null;
}

export interface CommercialMovementClient {
  client: string;
  gross: number;
  date: string;
}

export interface CommercialBillingDashboard {
  from: string;
  to: string;
  hasData: boolean;
  kpis: {
    gross: number;
    net: number;
    vmld: number;
    processes: number;
    activeClients: number;
    averageTicket: number;
  };
  monthly: { month: string; gross: number; net: number }[];
  topClients: CommercialClientRow[];
  concentration: { top1: number; top5: number; top10: number };
  byAgent: { agent: string; gross: number; net: number; clients: number }[];
  movements: {
    available: boolean;
    previousFrom: string;
    previousTo: string;
    new: CommercialMovementClient[];
    lost: CommercialMovementClient[];
  };
  clients: CommercialClientRow[];
  clientOptions: string[];
  agentOptions: string[];
}

export interface CommercialClientManagement {
  total: number;
  withResponsible: number;
  withAgent1: number;
  withAgent2: number;
  rows: {
    companyId: string;
    client: string;
    commercialResponsible?: string | null;
    agent1?: string | null;
    agent2?: string | null;
    lastBilling?: string | null;
  }[];
}

export const getCommercialBilling = async (
  params: CommercialBillingParams
): Promise<CommercialBillingDashboard> => {
  const response = await api.get(`/api/management/commercial/billing`, { params });
  return response.data;
};

export const getCommercialClients = async (companyId?: string): Promise<CommercialClientManagement> => {
  const response = await api.get(`/api/management/commercial/clients`, { params: { companyId } });
  return response.data;
};

// ── Distribuidora ────────────────────────────────────────────────────────────
export interface DistribuidoraParams {
  companyType?: number;
  year?: number;
  month?: number;
  client?: string;
  status?: "all" | "active" | "reversed";
  /** "Emular cliente". */
  companyId?: string;
}

export interface DistribuidoraInvoicesParams extends DistribuidoraParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export interface DistribuidoraRank {
  name: string;
  total: number;
  invoices: number;
}

export interface DistribuidoraDashboard {
  hasData: boolean;
  kpis: {
    total: number;
    products: number;
    processes: number;
    activeClients: number;
    averageTicket: number;
    taxes: number;
    taxesPercent: number;
    invoices: number;
    reversedInvoices: number;
    reversedTotal: number;
  };
  monthly: { month: string; total: number; invoices: number }[];
  byCompany: { companyType: number; company: string; total: number; invoices: number; share: number }[];
  topClients: DistribuidoraRank[];
  topProcesses: DistribuidoraRank[];
  taxes: { pis: number; cofins: number; ipi: number; icms: number; icmsStFcp: number };
  yearOptions: number[];
  clientOptions: string[];
}

export interface DistribuidoraInvoiceRow {
  id: string;
  issueDate: string;
  companyType: number;
  company: string;
  number: string;
  series?: string | null;
  client: string;
  processRef?: string | null;
  declarationNumber?: string | null;
  products: number;
  taxes: number;
  total: number;
  status: string;
  /** "imported" = planilha (Configurações › Importações); "system" = NF-e emitida pelo sistema. */
  source: "imported" | "system";
}

export interface DistribuidoraInvoicesPage {
  page: number;
  pageSize: number;
  totalCount: number;
  items: DistribuidoraInvoiceRow[];
}

export const getDistribuidoraDashboard = async (
  params: DistribuidoraParams
): Promise<DistribuidoraDashboard> => {
  const response = await api.get(`/api/management/distribuidora/dashboard`, { params });
  return response.data;
};

export const getDistribuidoraInvoices = async (
  params: DistribuidoraInvoicesParams
): Promise<DistribuidoraInvoicesPage> => {
  const response = await api.get(`/api/management/distribuidora/invoices`, { params });
  return response.data;
};
